import { useState, type CSSProperties, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PasswordInput } from '../../components/ui/PasswordInput'
import { AuthLayout, authFormStyles as s } from '@/components/auth/AuthLayout'
import { CodeInput } from '@/components/auth/CodeInput'
import { authApi } from '@/api/authApi'
import { ApiError } from '@/api/httpClient'
import { useCountdown } from '@/hooks/useCountdown'
import { morphToast } from '@/components/ui/Toast'

const CODE_LENGTH = 6
const MIN_PASSWORD_LENGTH = 8

export default function ResetPasswordPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [email, setEmail] = useState(searchParams.get('email') ?? '')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { seconds, start, isCounting } = useCountdown(0)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (password !== confirmation) {
      setError(t('auth.resetPassword.mismatch', 'As senhas não conferem.'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await authApi.confirmPasswordReset({ email, code, password })
      morphToast.success(t('auth.resetPassword.done', 'Senha alterada. Faça login novamente.'))
      navigate('/login', { replace: true })
    } catch (err) {
      const raw = err instanceof ApiError ? (err.raw as Record<string, unknown> | null) : null
      const remaining = raw?.remaining_attempts
      const base = err instanceof Error ? err.message : String(err)
      setError(
        typeof remaining === 'number' && remaining > 0
          ? t('auth.resetPassword.attemptsLeft', {
              defaultValue: '{message} Você ainda tem {attempts} tentativa(s).',
              message: base,
              attempts: remaining,
            })
          : base,
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResend() {
    if (isCounting || resending || !email) return
    setResending(true)
    setError(null)
    try {
      const response = await authApi.requestPasswordReset(email)
      setCode('')
      start(response.resend_in)
      morphToast.success(t('auth.resetPassword.resent', 'Se o e-mail existir, enviamos um novo código.'))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setResending(false)
    }
  }

  const canSubmit =
    !submitting &&
    Boolean(email) &&
    code.length === CODE_LENGTH &&
    password.length >= MIN_PASSWORD_LENGTH &&
    confirmation.length >= MIN_PASSWORD_LENGTH

  return (
    <AuthLayout
      title={t('auth.resetPassword.title', 'Criar nova senha')}
      subtitle={t('auth.resetPassword.subtitle', {
        defaultValue: 'Digite o código enviado para {email} e escolha a nova senha.',
        email,
      })}
      footer={
        <span>
          {t('auth.resetPassword.remembered', 'Lembrou da senha?')}{' '}
          <Link to="/login" style={{ color: 'var(--accent)' }}>
            {t('auth.resetPassword.login', 'Entrar')}
          </Link>
        </span>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error ? <div style={s.error}>{error}</div> : null}

        {searchParams.get('email') ? null : (
          <div style={s.field}>
            <label style={s.label} htmlFor="email">
              {t('auth.fields.email', 'E-mail')}
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              style={s.input}
            />
          </div>
        )}

        <CodeInput
          value={code}
          onChange={(next) => {
            setCode(next)
            if (error) setError(null)
          }}
          length={CODE_LENGTH}
          disabled={submitting}
          invalid={Boolean(error)}
          autoFocus
          label={t('auth.resetPassword.codeLabel', 'Código recebido por e-mail')}
        />

        <div style={s.field}>
          <label style={s.label} htmlFor="password">
            {t('auth.resetPassword.newPassword', 'Nova senha')}
          </label>
          <PasswordInput
            id="password"
            required
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            style={s.input}
          />
        </div>

        <div style={s.field}>
          <label style={s.label} htmlFor="confirmation">
            {t('auth.resetPassword.confirmPassword', 'Repita a nova senha')}
          </label>
          <PasswordInput
            id="confirmation"
            required
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            style={s.input}
          />
        </div>

        <button type="submit" style={{ ...s.button, opacity: canSubmit ? 1 : 0.65 }} disabled={!canSubmit}>
          {submitting
            ? t('auth.resetPassword.submitting', 'Salvando...')
            : t('auth.resetPassword.submit', 'Salvar nova senha')}
        </button>

        <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
          {t('auth.resetPassword.noCode', 'Não recebeu o código?')}{' '}
          {isCounting ? (
            <span>
              {t('auth.resetPassword.resendIn', { defaultValue: 'Reenviar em {seconds}s', seconds })}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => void handleResend()}
              disabled={resending || !email}
              style={linkButton}
            >
              {resending
                ? t('auth.resetPassword.resending', 'Enviando...')
                : t('auth.resetPassword.resend', 'Reenviar código')}
            </button>
          )}
        </div>
      </form>
    </AuthLayout>
  )
}

const linkButton: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  color: 'var(--accent)',
  cursor: 'pointer',
}
